import { useEffect, useState } from "react";
import { api, API_BASE, formatMoney, formatPrice, formatDate, todayISO, COMPANY_LOGO, openDownload } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, FileText, Download, Package, MessageCircle, Mail, Palette, Copy, Bookmark, CheckSquare, Square } from "lucide-react";

const priceText = (v) => (v ? Number(v).toFixed(7).replace(/\.?0+$/, "") : "");
const PriceInput = ({ value, onChange, testId }) => {
  const [text, setText] = useState(priceText(value));
  useEffect(() => { if (Number(text) !== Number(value || 0)) setText(priceText(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <input type="text" inputMode="decimal" data-testid={testId} placeholder="0" value={text}
      onChange={(e) => {
        const t = e.target.value.replace(",", ".");
        if (!/^\d*\.?\d{0,7}$/.test(t)) return;
        setText(t); onChange(Number(t) || 0);
      }}
      className="w-full px-2 py-1 border rounded font-mono-num text-right" />
  );
};

export default function Proforma() {
  const [list, setList] = useState([]);
  const [cari, setCari] = useState([]);
  const [products, setProducts] = useState([]);
  const [company, setCompany] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [presets, setPresets] = useState({ payment_terms: [], delivery_terms: [], notes: [] });
  const [presetMgrOpen, setPresetMgrOpen] = useState(false);
  const [newPreset, setNewPreset] = useState({ name: "", category: "payment_terms", text: "" });
  const [selected, setSelected] = useState(new Set());
  const [tplPickerOpen, setTplPickerOpen] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [saveTplName, setSaveTplName] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: "", message: "" });
  const [current, setCurrent] = useState(null);

  const emptyDoc = () => ({
    number: `PRF-${Date.now().toString().slice(-6)}`,
    date: todayISO(),
    valid_until: todayISO(),
    cari_id: "",
    customer_name: "", customer_address: "", customer_tax: "", customer_email: "",
    currency: "TRY",
    lines: [],
    discount: 0,
    notes: "",
    payment_terms: "Peşin / Havale",
    delivery_terms: "3-5 iş günü",
    template: "classic",
    language: "tr",
  });

  const load = async () => {
    const [l, c, p, co, t, ps] = await Promise.all([
      api.get("/proforma"), api.get("/cari"), api.get("/products"), api.get("/company"), api.get("/proforma-templates"),
      api.get("/text-presets"),
    ]);
    setList(l.data); setCari(c.data); setProducts(p.data); setCompany(co.data); setTemplates(t.data);
    const grouped = { payment_terms: [], delivery_terms: [], notes: [] };
    for (const p of ps.data) if (grouped[p.category]) grouped[p.category].push(p);
    setPresets(grouped);
  };
  useEffect(() => { load(); }, []);

  const newDraft = async () => {
    const { data } = await api.get("/proforma-next-number");
    setCurrent({ ...emptyDoc(), number: data.number });
    setEditorOpen(true);
  };

  const applyTemplate = async (tpl) => {
    const { data } = await api.get("/proforma-next-number");
    const { id, name, created_at, ...clean } = tpl;
    setCurrent({
      ...emptyDoc(),
      ...clean,
      number: data.number,
      date: todayISO(),
      valid_until: todayISO(),
    });
    setTplPickerOpen(false);
    setEditorOpen(true);
    toast.success(`Şablon uygulandı: ${tpl.name}`);
  };

  const saveAsTemplate = async () => {
    if (!saveTplName.trim()) { toast.error("İsim girin"); return; }
    try {
      if (current.id) {
        await api.post("/proforma-templates", { name: saveTplName, proforma_id: current.id });
      } else {
        await api.post("/proforma-templates", { name: saveTplName, data: current });
      }
      toast.success("Şablon kaydedildi");
      setSaveTplOpen(false); setSaveTplName("");
      const t = await api.get("/proforma-templates"); setTemplates(t.data);
    } catch { toast.error("Kayıt başarısız"); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Şablon silinsin mi?")) return;
    await api.delete(`/proforma-templates/${id}`);
    const t = await api.get("/proforma-templates"); setTemplates(t.data);
    toast.success("Şablon silindi");
  };

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === list.length) setSelected(new Set());
    else setSelected(new Set(list.map(p => p.id)));
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} proforma silinsin mi?`)) return;
    await api.post("/proforma/bulk-delete", { ids: [...selected] });
    toast.success(`${selected.size} kayıt silindi`);
    setSelected(new Set()); load();
  };
  const bulkPdf = async () => {
    if (selected.size === 0) return;
    try {
      const res = await api.post("/proforma/bulk-pdf", { ids: [...selected] }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Proformalar-${todayISO()}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${selected.size} PDF ZIP olarak indirildi`);
    } catch { toast.error("İndirme başarısız"); }
  };
  const bulkEmail = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} proforma müşteri e-postalarına gönderilsin mi?`)) return;
    try {
      const { data } = await api.post("/proforma/bulk-email", { ids: [...selected] });
      toast.success(`${data.sent_count} gönderildi, ${data.failed_count} başarısız`);
      setSelected(new Set());
    } catch { toast.error("Toplu gönderim başarısız"); }
  };

  const addPreset = async () => {
    if (!newPreset.name || !newPreset.text) { toast.error("İsim ve metin girin"); return; }
    try {
      await api.post("/text-presets", newPreset);
      toast.success("Metin şablonu kaydedildi");
      setNewPreset({ name: "", category: newPreset.category, text: "" });
      const ps = await api.get("/text-presets");
      const grouped = { payment_terms: [], delivery_terms: [], notes: [] };
      for (const p of ps.data) if (grouped[p.category]) grouped[p.category].push(p);
      setPresets(grouped);
    } catch { toast.error("Kayıt başarısız"); }
  };
  const delPreset = async (id) => {
    await api.delete(`/text-presets/${id}`);
    const ps = await api.get("/text-presets");
    const grouped = { payment_terms: [], delivery_terms: [], notes: [] };
    for (const p of ps.data) if (grouped[p.category]) grouped[p.category].push(p);
    setPresets(grouped);
    toast.success("Silindi");
  };
  const editDoc = async (id) => {
    const { data } = await api.get(`/proforma/${id}`);
    setCurrent(data); setEditorOpen(true);
  };

  const save = async () => {
    try {
      if (current.id) await api.put(`/proforma/${current.id}`, current);
      else await api.post("/proforma", current);
      toast.success("Proforma kaydedildi");
      setEditorOpen(false); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/proforma/${id}`); toast.success("Silindi"); load();
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/proforma/${id}/duplicate`);
      toast.success(`Kopyalandı: ${data.number}`);
      await load();
      // Open the new proforma in editor
      setCurrent(data);
      setEditorOpen(true);
    } catch { toast.error("Kopyalama başarısız"); }
  };

  const openPdf = (id) => {
    const token = localStorage.getItem("madymark_token");
    openDownload(`/api/proforma/${id}/pdf`);
  };

  const shareWhatsApp = async (p) => {
    try {
      const { data } = await api.post(`/proforma/${p.id}/share`);
      const publicUrl = `${API_BASE}/api/proforma/public/${p.id}.pdf?token=${data.share_token}`;
      const total = p.lines.reduce((s, l) => s + l.quantity * l.unit_price * (1 + l.tax_rate/100), 0) - (p.discount || 0);
      const msg =
        `*MADYMARK - Proforma Fatura*\n\n` +
        `Sayın ${p.customer_name || "Müşterimiz"},\n\n` +
        `Talebiniz üzerine hazırladığımız proforma faturamızı ilettim.\n\n` +
        `• Belge No: *${p.number}*\n` +
        `• Tarih: ${formatDate(p.date)}\n` +
        `• Geçerlilik: ${formatDate(p.valid_until)}\n` +
        `• Toplam: *${formatMoney(total, p.currency)}*\n\n` +
        `PDF: ${publicUrl}\n\n` +
        `MADYMARK İÇ VE DIŞ TİCARET SANAYİ A.Ş.`;
      const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank");
      toast.success("WhatsApp açıldı");
    } catch {
      toast.error("Paylaşım başarısız");
    }
  };

  const openEmailModal = (p) => {
    setCurrent(p);
    setEmailForm({ to: p.customer_email || "", message: "" });
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    try {
      const { data } = await api.post(`/proforma/${current.id}/email`, emailForm);
      toast.success(`E-posta gönderildi: ${data.to}`);
      setEmailOpen(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "E-posta gönderilemedi");
    }
  };

  const setCariField = (id) => {
    const c = cari.find(x => x.id === id);
    if (c) setCurrent({ ...current, cari_id: id, customer_name: c.name, customer_address: c.address, customer_tax: c.tax_number, customer_email: c.email, currency: c.currency });
    else setCurrent({ ...current, cari_id: id });
  };

  const addLine = (p) => {
    const line = { _uid: (crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`),
      product_id: p.id, name: p.name, description: p.description || "", hs_code: p.hs_code || "",
      quantity: 1, unit: p.unit || "adet",
      unit_price: p.price, tax_rate: p.vat_rate || 20, image_url: p.image_url || "" };
    setCurrent({ ...current, lines: [...current.lines, line] });
  };
  const addBlankLine = () => setCurrent({ ...current, lines: [...current.lines, { _uid: (crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`), name: "", description: "", hs_code: "", quantity: 1, unit: "adet", unit_price: 0, tax_rate: 20, image_url: "" }] });
  const updateLine = (i, k, v) => {
    const lines = [...current.lines]; lines[i] = { ...lines[i], [k]: v }; setCurrent({ ...current, lines });
  };
  const removeLine = (i) => setCurrent({ ...current, lines: current.lines.filter((_, ix) => ix !== i) });

  const subtotal = (current?.lines || []).reduce((s, l) => s + l.quantity * l.unit_price, 0);
  const taxTotal = (current?.lines || []).reduce((s, l) => s + l.quantity * l.unit_price * (l.tax_rate/100), 0);
  const grand = subtotal + taxTotal - Number(current?.discount || 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Belgeler</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">Proforma Faturalar</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="preset-mgr" onClick={() => setPresetMgrOpen(true)} className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white border border-slate-200 hover:border-[#29B7E3] text-slate-700 hover:text-[#1E2A9C] font-bold transition">
            <FileText className="w-4 h-4" /> Metin Şablonları
          </button>
          {templates.length > 0 && (
            <button data-testid="template-picker" onClick={() => setTplPickerOpen(true)} className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white border border-slate-200 hover:border-[#29B7E3] text-slate-700 hover:text-[#1E2A9C] font-bold transition">
              <Bookmark className="w-4 h-4" /> Şablondan ({templates.length})
            </button>
          )}
          <button data-testid="new-proforma" onClick={newDraft} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] shadow-lg shadow-[#1E2A9C]/20"><Plus className="w-4 h-4" /> Yeni Proforma</button>
        </div>
      </div>

      {selected.size > 0 && (
        <div data-testid="bulk-bar" className="flex items-center justify-between bg-[#1E2A9C] text-white px-5 py-3 rounded-xl shadow-lg shadow-[#1E2A9C]/20 fade-up">
          <div className="text-sm font-bold">{selected.size} kayıt seçildi</div>
          <div className="flex gap-2">
            <button data-testid="bulk-pdf" onClick={bulkPdf} className="px-3 py-1.5 bg-white/20 rounded-lg text-sm font-bold hover:bg-white/30 flex items-center gap-1"><Download className="w-3.5 h-3.5" /> PDF ZIP</button>
            <button data-testid="bulk-email" onClick={bulkEmail} className="px-3 py-1.5 bg-white/20 rounded-lg text-sm font-bold hover:bg-white/30 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Toplu E-posta</button>
            <button data-testid="bulk-delete" onClick={bulkDelete} className="px-3 py-1.5 bg-rose-500 rounded-lg text-sm font-bold hover:bg-rose-600 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Sil</button>
            <button onClick={() => setSelected(new Set())} className="px-3 py-1.5 rounded-lg text-sm hover:bg-white/10">Vazgeç</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="w-10 px-4 py-3">
                <button data-testid="select-all" onClick={toggleAll} className="text-slate-400 hover:text-[#1E2A9C]">
                  {selected.size === list.length && list.length > 0 ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                </button>
              </th>
              <th className="text-left px-6 py-3 font-bold">No</th>
              <th className="text-left px-6 py-3 font-bold">Tarih</th>
              <th className="text-left px-6 py-3 font-bold">Müşteri</th>
              <th className="text-left px-6 py-3 font-bold">Kalem</th>
              <th className="text-right px-6 py-3 font-bold">Tutar</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="text-center py-16 text-slate-400">Henüz proforma yok</td></tr>}
            {list.map(p => {
              const st = p.lines.reduce((s, l) => s + l.quantity * l.unit_price, 0);
              const tt = p.lines.reduce((s, l) => s + l.quantity * l.unit_price * (l.tax_rate/100), 0);
              const gr = st + tt - (p.discount || 0);
              const isSelected = selected.has(p.id);
              return (
                <tr key={p.id} className={`border-t border-slate-100 hover:bg-slate-50/50 ${isSelected ? "bg-blue-50/50" : ""}`}>
                  <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                    <button data-testid={`sel-${p.id}`} onClick={() => toggleSelect(p.id)} className={isSelected ? "text-[#1E2A9C]" : "text-slate-300 hover:text-slate-500"}>
                      {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-6 py-4 font-mono-num font-bold text-[#1E2A9C] cursor-pointer" onClick={() => editDoc(p.id)}>{p.number}</td>
                  <td className="px-6 py-4 text-slate-700 cursor-pointer" onClick={() => editDoc(p.id)}>{formatDate(p.date)}</td>
                  <td className="px-6 py-4 font-semibold text-slate-800 cursor-pointer" onClick={() => editDoc(p.id)}>{p.customer_name}</td>
                  <td className="px-6 py-4 text-slate-600 cursor-pointer" onClick={() => editDoc(p.id)}>{p.lines.length}</td>
                  <td className="px-6 py-4 text-right font-mono-num font-bold text-slate-900 cursor-pointer" onClick={() => editDoc(p.id)}>{formatMoney(gr, p.currency)}</td>
                  <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-2">
                      <button data-testid={`dup-${p.id}`} onClick={() => duplicate(p.id)} className="text-slate-500 hover:text-[#1E2A9C]" title="Kopyala"><Copy className="w-4 h-4" /></button>
                      <button data-testid={`email-${p.id}`} onClick={() => openEmailModal(p)} className="text-[#29B7E3] hover:text-[#1E90BF]" title="E-posta Gönder"><Mail className="w-4 h-4" /></button>
                      <button data-testid={`whatsapp-${p.id}`} onClick={() => shareWhatsApp(p)} className="text-emerald-600 hover:text-emerald-700" title="WhatsApp Paylaş"><MessageCircle className="w-4 h-4" /></button>
                      <button data-testid={`pdf-${p.id}`} onClick={() => openPdf(p.id)} className="text-[#1E2A9C] hover:text-[#29B7E3]" title="PDF"><Download className="w-4 h-4" /></button>
                      <button data-testid={`del-prf-${p.id}`} onClick={() => del(p.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editorOpen && current && (
        <div className="fixed inset-0 bg-white z-50 flex flex-col">
          <div className="p-6 border-b flex items-center justify-between bg-white flex-shrink-0">
              <div className="flex items-center gap-3">
                <img src={COMPANY_LOGO} alt="" className="w-10 h-10 object-contain" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Proforma Fatura</div>
                  <div className="font-display font-extrabold text-xl">{current.number}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button data-testid="save-template" onClick={() => setSaveTplOpen(true)} className="px-4 py-2 border border-slate-200 rounded-lg font-bold text-sm flex items-center gap-1 text-slate-700 hover:border-[#1E2A9C]"><Bookmark className="w-4 h-4" /> Şablon</button>
                <button data-testid="save-proforma" onClick={save} className="px-4 py-2 bg-[#1E2A9C] text-white rounded-lg font-bold text-sm">Kaydet</button>
                {current.id && <button data-testid="pdf-editor" onClick={() => openPdf(current.id)} className="px-4 py-2 border border-slate-200 rounded-lg font-bold text-sm flex items-center gap-1"><Download className="w-4 h-4" />PDF</button>}
                {current.id && <button data-testid="email-editor" onClick={() => openEmailModal(current)} className="px-4 py-2 bg-[#29B7E3] text-white rounded-lg font-bold text-sm flex items-center gap-1"><Mail className="w-4 h-4" />E-posta</button>}
                {current.id && <button data-testid="whatsapp-editor" onClick={() => shareWhatsApp(current)} className="px-4 py-2 bg-emerald-500 text-white rounded-lg font-bold text-sm flex items-center gap-1"><MessageCircle className="w-4 h-4" />WhatsApp</button>}
                <button onClick={() => setEditorOpen(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="p-8 space-y-6 overflow-y-auto flex-1">
              <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <Palette className="w-4 h-4 text-[#1E2A9C]" />
                  <div className="text-xs uppercase font-bold text-slate-500 tracking-widest">Fatura Şablonu</div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: "classic", name: "Klasik", desc: "Lacivert antet · Grid · Kurumsal" },
                    { key: "modern", name: "Modern", desc: "Cyan aksan · Yumuşak · Minimalist" },
                    { key: "minimal", name: "Minimal", desc: "İnce çizgi · Beyaz · Zarif" },
                  ].map(t => (
                    <button
                      key={t.key}
                      type="button"
                      data-testid={`tpl-${t.key}`}
                      onClick={() => setCurrent({ ...current, template: t.key })}
                      className={`p-3 rounded-xl text-left transition ${
                        (current.template || "classic") === t.key
                          ? "bg-white border-2 border-[#1E2A9C] shadow-md"
                          : "bg-white border border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className={`h-16 rounded-lg mb-2 relative overflow-hidden ${
                        t.key === "classic" ? "bg-white border border-slate-200" :
                        t.key === "modern" ? "bg-white border border-slate-200" :
                        "bg-white border border-slate-100"
                      }`}>
                        {t.key === "classic" && (<>
                          <div className="absolute top-0 inset-x-0 h-3 bg-[#1E2A9C]" />
                          <div className="absolute top-5 left-2 right-2 h-1 bg-slate-200 rounded" />
                          <div className="absolute top-7 left-2 right-2 h-1 bg-slate-200 rounded" />
                          <div className="absolute bottom-2 right-2 w-10 h-3 bg-[#1E2A9C] rounded" />
                        </>)}
                        {t.key === "modern" && (<>
                          <div className="absolute top-3 left-2 right-2 h-1 bg-slate-800 rounded" />
                          <div className="absolute top-6 inset-x-2 h-0.5 bg-[#29B7E3]" />
                          <div className="absolute top-8 left-2 right-2 h-1 bg-slate-200 rounded" />
                          <div className="absolute bottom-2 right-2 w-10 h-3 bg-[#29B7E3] rounded" />
                        </>)}
                        {t.key === "minimal" && (<>
                          <div className="absolute top-3 left-2 right-2 h-0.5 bg-slate-900" />
                          <div className="absolute top-5 left-2 right-2 h-1 bg-slate-200 rounded" />
                          <div className="absolute top-8 left-2 right-2 h-0.5 bg-slate-200" />
                          <div className="absolute bottom-2 right-2 w-10 h-3 bg-slate-900 rounded" />
                        </>)}
                      </div>
                      <div className="text-sm font-bold font-display">{t.name}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-5 gap-4">
                <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">No</label>
                  <input data-testid="prf-number" value={current.number} onChange={(e) => setCurrent({...current, number: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
                <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Tarih</label>
                  <input type="date" data-testid="prf-date" value={current.date} onChange={(e) => setCurrent({...current, date: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
                <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Geçerlilik</label>
                  <input type="date" data-testid="prf-valid" value={current.valid_until} onChange={(e) => setCurrent({...current, valid_until: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
                <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Para Birimi</label>
                  <select data-testid="prf-currency" value={current.currency} onChange={(e) => setCurrent({...current, currency: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                    {["TRY","USD","EUR"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">PDF Dili</label>
                  <div className="mt-1 inline-flex rounded-lg border border-slate-200 overflow-hidden w-full">
                    {[{k:"tr",label:"🇹🇷 TR"},{k:"en",label:"🇬🇧 EN"}].map(o => (
                      <button
                        key={o.k}
                        type="button"
                        data-testid={`prf-lang-${o.k}`}
                        onClick={() => setCurrent({...current, language: o.k})}
                        className={`flex-1 px-3 py-2 text-sm font-bold transition ${
                          (current.language || "tr") === o.k
                            ? "bg-[#1E2A9C] text-white"
                            : "bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >{o.label}</button>
                    ))}
                  </div></div>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="text-xs uppercase font-bold text-slate-500 tracking-widest mb-3">Müşteri Bilgileri</div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-xs text-slate-500">Kayıtlı Cari Seç</label>
                    <select data-testid="prf-cari" value={current.cari_id} onChange={(e) => setCariField(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-lg">
                      <option value="">Manuel Gir</option>
                      {cari.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select></div>
                  <div><label className="text-xs text-slate-500">Ünvan</label>
                    <input data-testid="prf-cust-name" value={current.customer_name} onChange={(e) => setCurrent({...current, customer_name: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
                  <div><label className="text-xs text-slate-500">VKN</label>
                    <input data-testid="prf-cust-tax" value={current.customer_tax} onChange={(e) => setCurrent({...current, customer_tax: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
                  <div><label className="text-xs text-slate-500">E-posta</label>
                    <input data-testid="prf-cust-email" value={current.customer_email} onChange={(e) => setCurrent({...current, customer_email: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
                  <div className="col-span-2"><label className="text-xs text-slate-500">Adres</label>
                    <textarea data-testid="prf-cust-addr" value={current.customer_address} onChange={(e) => setCurrent({...current, customer_address: e.target.value})} rows="2" className="w-full mt-1 px-3 py-2 border rounded-lg"></textarea></div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase font-bold text-slate-500 tracking-widest">Kalemler</div>
                  <div className="flex gap-2">
                    <button data-testid="pick-product" type="button" onClick={() => setPickerOpen(true)} className="px-3 py-1.5 bg-[#29B7E3] text-white rounded-lg text-xs font-bold flex items-center gap-1"><Package className="w-3.5 h-3.5" /> Ürün Seç</button>
                    <button data-testid="add-blank" type="button" onClick={addBlankLine} className="px-3 py-1.5 bg-slate-100 rounded-lg text-xs font-bold flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Boş Satır</button>
                  </div>
                </div>
                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-widest">
                      <tr>
                        <th className="text-left px-3 py-2 font-bold">Görsel</th>
                        <th className="text-left px-3 py-2 font-bold">Ürün</th>
                        <th className="text-right px-3 py-2 font-bold w-20">Miktar</th>
                        <th className="text-left px-3 py-2 font-bold w-20">Birim</th>
                        <th className="text-right px-3 py-2 font-bold w-40 min-w-[140px]">Fiyat</th>
                        <th className="text-right px-3 py-2 font-bold w-20">KDV %</th>
                        <th className="text-right px-3 py-2 font-bold w-32">Toplam</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {current.lines.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400 text-sm">Kalem ekleyin</td></tr>}
                      {current.lines.map((l, i) => {
                        const t = l.quantity * l.unit_price * (1 + l.tax_rate/100);
                        return (
                          <tr key={l._uid || i} className="border-t">
                            <td className="px-3 py-2">
                              <div className="w-12 h-12 bg-slate-100 rounded overflow-hidden grid place-items-center">
                                {l.image_url ? <img src={l.image_url} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-slate-300" />}
                              </div>
                            </td>
                            <td className="px-3 py-2"><input value={l.name} onChange={(e) => updateLine(i, "name", e.target.value)} className="w-full px-2 py-1 border rounded" /></td>
                            <td className="px-3 py-2"><input type="number" step="0.01" value={l.quantity} onChange={(e) => updateLine(i, "quantity", Number(e.target.value))} className="w-full px-2 py-1 border rounded font-mono-num text-right" /></td>
                            <td className="px-3 py-2"><input value={l.unit} onChange={(e) => updateLine(i, "unit", e.target.value)} className="w-full px-2 py-1 border rounded" /></td>
                            <td className="px-3 py-2"><PriceInput testId={`prf-line-price-${i}`} value={l.unit_price} onChange={(v) => updateLine(i, "unit_price", v)} /></td>
                            <td className="px-3 py-2"><input type="number" value={l.tax_rate} onChange={(e) => updateLine(i, "tax_rate", Number(e.target.value))} className="w-full px-2 py-1 border rounded font-mono-num text-right" /></td>
                            <td className="px-3 py-2 text-right font-mono-num font-bold">{formatMoney(t, current.currency)}</td>
                            <td className="px-3 py-2"><button onClick={() => removeLine(i)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Ödeme Koşulları</label>
                      {presets.payment_terms.length > 0 && (
                        <select data-testid="preset-payment" onChange={(e) => { if (e.target.value) { setCurrent({...current, payment_terms: e.target.value}); e.target.value = ""; } }} className="text-[10px] px-2 py-1 border rounded">
                          <option value="">Şablon seç...</option>
                          {presets.payment_terms.map(p => <option key={p.id} value={p.text}>{p.name}</option>)}
                        </select>
                      )}
                    </div>
                    <input data-testid="prf-payment" value={current.payment_terms} onChange={(e) => setCurrent({...current, payment_terms: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Teslimat</label>
                      {presets.delivery_terms.length > 0 && (
                        <select data-testid="preset-delivery" onChange={(e) => { if (e.target.value) { setCurrent({...current, delivery_terms: e.target.value}); e.target.value = ""; } }} className="text-[10px] px-2 py-1 border rounded">
                          <option value="">Şablon seç...</option>
                          {presets.delivery_terms.map(p => <option key={p.id} value={p.text}>{p.name}</option>)}
                        </select>
                      )}
                    </div>
                    <input data-testid="prf-delivery" value={current.delivery_terms} onChange={(e) => setCurrent({...current, delivery_terms: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Notlar</label>
                      {presets.notes.length > 0 && (
                        <select data-testid="preset-notes" onChange={(e) => { if (e.target.value) { setCurrent({...current, notes: e.target.value}); e.target.value = ""; } }} className="text-[10px] px-2 py-1 border rounded">
                          <option value="">Şablon seç...</option>
                          {presets.notes.map(p => <option key={p.id} value={p.text}>{p.name}</option>)}
                        </select>
                      )}
                    </div>
                    <textarea data-testid="prf-notes" value={current.notes} onChange={(e) => setCurrent({...current, notes: e.target.value})} rows="3" className="w-full mt-1 px-3 py-2 border rounded-lg"></textarea>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-xl p-6 border">
                  <div className="space-y-2 font-mono-num">
                    <div className="flex justify-between text-slate-600"><span>Ara Toplam</span><span>{formatMoney(subtotal, current.currency)}</span></div>
                    <div className="flex justify-between text-slate-600"><span>KDV</span><span>{formatMoney(taxTotal, current.currency)}</span></div>
                    <div className="flex justify-between items-center text-slate-600"><span>İndirim</span>
                      <input type="number" step="0.01" data-testid="prf-discount" value={current.discount} onChange={(e) => setCurrent({...current, discount: Number(e.target.value)})} className="w-28 px-2 py-1 border rounded text-right" /></div>
                    <div className="pt-3 mt-3 border-t border-slate-200 flex justify-between items-center">
                      <span className="text-xs uppercase font-bold text-slate-500 tracking-widest">Genel Toplam</span>
                      <span className="font-display text-2xl font-extrabold text-[#1E2A9C]">{formatMoney(grand, current.currency)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      )}

      {pickerOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] grid place-items-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl font-extrabold">Ürün Seç</div>
              <button onClick={() => setPickerOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="mb-4">
              <input
                data-testid="barcode-search"
                autoFocus
                placeholder="Barkod okutun veya isim/SKU ile arayın..."
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-[#1E2A9C] outline-none font-mono-num"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const q = e.target.value.trim().toLowerCase();
                    if (!q) return;
                    const exact = products.find(p => (p.barcode || "").toLowerCase() === q);
                    if (exact) { addLine(exact); setPickerOpen(false); toast.success(`Eklendi: ${exact.name}`); return; }
                    const matches = products.filter(p =>
                      (p.name || "").toLowerCase().includes(q) ||
                      (p.sku || "").toLowerCase().includes(q) ||
                      (p.barcode || "").toLowerCase().includes(q)
                    );
                    if (matches.length === 1) { addLine(matches[0]); setPickerOpen(false); toast.success(`Eklendi: ${matches[0].name}`); }
                    else if (matches.length === 0) toast.error("Ürün bulunamadı");
                  }
                }}
                onInput={(e) => {
                  const q = e.target.value.trim().toLowerCase();
                  document.querySelectorAll("[data-picker-item]").forEach(el => {
                    const name = (el.getAttribute("data-name") || "").toLowerCase();
                    const sku = (el.getAttribute("data-sku") || "").toLowerCase();
                    const bc = (el.getAttribute("data-barcode") || "").toLowerCase();
                    el.style.display = (!q || name.includes(q) || sku.includes(q) || bc.includes(q)) ? "" : "none";
                  });
                }}
              />
              <div className="text-xs text-slate-500 mt-1">💡 Barkod okuyucusu bu alana yazdıktan sonra Enter'a basar. Tek eşleşme otomatik eklenir.</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {products.length === 0 && <div className="col-span-full text-center py-12 text-slate-400 text-sm">Önce ürün ekleyin</div>}
              {products.map(p => (
                <button key={p.id} data-testid={`pick-${p.id}`} data-picker-item data-name={p.name} data-sku={p.sku} data-barcode={p.barcode} onClick={() => { addLine(p); setPickerOpen(false); toast.success("Eklendi"); }} className="text-left p-3 border border-slate-200 rounded-xl hover:border-[#1E2A9C] hover:shadow transition group">
                  <div className="aspect-square bg-slate-100 rounded overflow-hidden mb-2 grid place-items-center">
                    {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition" /> : <Package className="w-8 h-8 text-slate-300" />}
                  </div>
                  <div className="text-xs font-semibold line-clamp-2">{p.name}</div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="text-xs text-[#1E2A9C] font-mono-num font-bold">{formatPrice(p.price, p.currency)}</div>
                    {p.barcode && <div className="text-[9px] font-mono-num text-slate-400">📊</div>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {presetMgrOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] grid place-items-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Metin Kütüphanesi</div>
                <div className="font-display text-2xl font-extrabold">Ödeme / Teslimat / Not Şablonları</div>
              </div>
              <button onClick={() => setPresetMgrOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border mb-5">
              <div className="text-xs uppercase font-bold text-slate-500 tracking-widest mb-3">Yeni Şablon Ekle</div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="np-name" placeholder="İsim (örn. Peşin Havale)" value={newPreset.name} onChange={(e) => setNewPreset({...newPreset, name: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" />
                <select data-testid="np-cat" value={newPreset.category} onChange={(e) => setNewPreset({...newPreset, category: e.target.value})} className="px-3 py-2 border rounded-lg text-sm">
                  <option value="payment_terms">Ödeme Koşulları</option>
                  <option value="delivery_terms">Teslimat</option>
                  <option value="notes">Notlar</option>
                </select>
              </div>
              <textarea data-testid="np-text" placeholder="Şablon metni..." value={newPreset.text} onChange={(e) => setNewPreset({...newPreset, text: e.target.value})} rows="2" className="w-full mt-2 px-3 py-2 border rounded-lg text-sm"></textarea>
              <button data-testid="np-add" onClick={addPreset} className="mt-2 px-4 py-2 bg-[#1E2A9C] text-white rounded-lg text-sm font-bold hover:bg-[#121A68]">+ Ekle</button>
            </div>

            {[
              { key: "payment_terms", label: "Ödeme Koşulları" },
              { key: "delivery_terms", label: "Teslimat" },
              { key: "notes", label: "Notlar" },
            ].map(cat => (
              <div key={cat.key} className="mb-5">
                <div className="text-xs uppercase font-bold text-slate-500 tracking-widest mb-2">{cat.label} ({presets[cat.key].length})</div>
                <div className="space-y-2">
                  {presets[cat.key].length === 0 && <div className="text-xs text-slate-400 py-2">Şablon yok</div>}
                  {presets[cat.key].map(p => (
                    <div key={p.id} className="p-3 border border-slate-200 rounded-lg flex items-start justify-between gap-3 hover:border-slate-300">
                      <div className="flex-1">
                        <div className="text-sm font-bold text-slate-800">{p.name}</div>
                        <div className="text-xs text-slate-600 mt-1 line-clamp-2">{p.text}</div>
                      </div>
                      <button onClick={() => delPreset(p.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tplPickerOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] grid place-items-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Şablon Seç</div>
                <div className="font-display text-2xl font-extrabold">Kayıtlı Şablonlar</div>
              </div>
              <button onClick={() => setTplPickerOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {templates.length === 0 && <div className="col-span-full text-center py-12 text-slate-400 text-sm">Henüz şablon yok</div>}
              {templates.map(t => {
                const lineCount = (t.lines || []).length;
                return (
                  <div key={t.id} className="p-4 border border-slate-200 rounded-xl hover:border-[#1E2A9C] hover:shadow transition group relative">
                    <button onClick={() => deleteTemplate(t.id)} className="absolute top-2 right-2 text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition"><Trash2 className="w-3.5 h-3.5" /></button>
                    <div className="flex items-center gap-2 mb-2">
                      <Bookmark className="w-4 h-4 text-[#1E2A9C]" />
                      <div className="font-bold font-display">{t.name}</div>
                    </div>
                    <div className="text-xs text-slate-500 mb-3">
                      {lineCount} kalem · {t.currency || "TRY"} · {t.template || "classic"} şablon
                      {t.customer_name && <> · <span className="font-semibold text-slate-700">{t.customer_name}</span></>}
                    </div>
                    <button data-testid={`use-tpl-${t.id}`} onClick={() => applyTemplate(t)} className="w-full py-2 bg-[#1E2A9C] text-white rounded-lg text-xs font-bold hover:bg-[#121A68]">Bu Şablonu Kullan</button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {saveTplOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[70] grid place-items-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-8 relative">
            <button onClick={() => setSaveTplOpen(false)} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#1E2A9C]/10 grid place-items-center"><Bookmark className="w-5 h-5 text-[#1E2A9C]" /></div>
              <div className="font-display text-2xl font-extrabold">Şablon Olarak Kaydet</div>
            </div>
            <p className="text-sm text-slate-500 mb-5">Bu proformayı ileride tek tıkla yeniden kullanmak için isimlendirin.</p>
            <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Şablon Adı</label>
            <input required data-testid="tpl-name" value={saveTplName} onChange={(e) => setSaveTplName(e.target.value)} placeholder="Standart Satış, Lojistik Hizmet..." className="w-full mt-1 px-3 py-2 border rounded-lg" />
            <button data-testid="tpl-save-btn" onClick={saveAsTemplate} className="w-full mt-5 py-3 bg-[#1E2A9C] text-white rounded-lg font-bold">Kaydet</button>
          </div>
        </div>
      )}

      {emailOpen && current && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[70] grid place-items-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-8 relative">
            <button onClick={() => setEmailOpen(false)} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-full bg-[#29B7E3]/10 grid place-items-center"><Mail className="w-5 h-5 text-[#29B7E3]" /></div>
              <div className="font-display text-2xl font-extrabold">Proforma E-postası</div>
            </div>
            <p className="text-sm text-slate-500 mb-5">Belge: <span className="font-mono-num font-bold text-[#1E2A9C]">{current.number}</span> · Şablon: <b>{current.template || "classic"}</b></p>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Alıcı E-posta</label>
                <input required type="email" data-testid="email-to" value={emailForm.to} onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded-lg" placeholder="musteri@sirket.com" />
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Özel Mesaj (opsiyonel)</label>
                <textarea data-testid="email-message" rows="3" value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded-lg" placeholder="Sayın müşterimiz, proformamızı ekte iletiyoruz..."></textarea>
              </div>
              <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg border border-slate-200">
                E-postada MADYMARK logolu profesyonel bir bildirim ile PDF indirme bağlantısı yer alacak. Alıcı bağlantıya tıklayarak proforma PDF'ini görüntüleyebilir.
              </div>
              <button data-testid="email-send" onClick={sendEmail} className="w-full py-3 bg-[#1E2A9C] text-white rounded-lg font-bold hover:bg-[#121A68] flex items-center justify-center gap-2">
                <Mail className="w-4 h-4" /> E-postayı Gönder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
